import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Save,
  SkipForward,
  Timer,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import CoworkingTaskAvatar from "../components/CoworkingTaskAvatar";
import { api } from "../lib/api";
import { formatCoworkingMmss, progressFromTimer, remainingFromTimer } from "../lib/coworkingTimer";

const TICK_MS = 250;

const inputClass =
  "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";
const primaryButton =
  "rounded bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-50";
const secondaryButton =
  "rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] hover:text-white disabled:opacity-50";

const REDEPLOY_HINT =
  "API route not found (404). Pull latest main on the PC and run deploy-backend.cmd so api.thebarracksrat.com matches GitHub.";

function taskLabel(task) {
  return task?.preferred_name || task?.display_name || task?.login || "viewer";
}

function apiErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return "";
}

function coworkingApiError(err, fallback) {
  const status = err?.response?.status;
  if (status === 404) return REDEPLOY_HINT;
  return apiErrorDetail(err) || fallback;
}

function configFromPayload(payload, current = {}) {
  return {
    enabled: Boolean(payload?.config?.enabled),
    work_minutes: payload?.config?.work_minutes ?? current.work_minutes ?? 25,
    break_minutes: payload?.config?.break_minutes ?? current.break_minutes ?? 5,
    max_tasks_per_viewer: payload?.config?.max_tasks_per_viewer ?? current.max_tasks_per_viewer ?? 3,
    max_open_tasks: payload?.config?.max_open_tasks ?? current.max_open_tasks ?? 40,
    announce_phase_changes: payload?.config?.announce_phase_changes !== false,
    confetti_sound: payload?.config?.confetti_sound !== false,
  };
}

function buildCoworkingPatch(config) {
  return {
    enabled: Boolean(config.enabled),
    work_minutes: Number(config.work_minutes || 25),
    break_minutes: Number(config.break_minutes || 5),
    max_tasks_per_viewer: Number(config.max_tasks_per_viewer || 3),
    confetti_sound: Boolean(config.confetti_sound),
  };
}

export default function CoworkingAdmin() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState({
    enabled: false,
    work_minutes: 25,
    break_minutes: 5,
    max_tasks_per_viewer: 3,
    confetti_sound: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const rawTimer = data?.timer || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/coworking");
      setData(payload);
      setConfig((current) => configFromPayload(payload, current));
    } catch (err) {
      toast.error(coworkingApiError(err, "Could not load coworking."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      api
        .get("/admin/coworking")
        .then(({ data: payload }) => setData(payload))
        .catch(() => {});
    }, 2000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (String(rawTimer?.status || "") !== "running") return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [rawTimer?.status, rawTimer?.ends_at, rawTimer?.remaining_seconds, rawTimer?.server_now]);

  const timer = useMemo(() => {
    const base = rawTimer || {};
    const remaining = remainingFromTimer(base, nowMs);
    return {
      ...base,
      remaining_seconds: remaining,
      display: formatCoworkingMmss(remaining),
      progress: progressFromTimer(base, remaining),
    };
  }, [rawTimer, nowMs]);

  const applyAdminPayload = (payload) => {
    setData((current) => ({
      ...(current || {}),
      ...payload,
      config: payload.config,
      recent_tasks: current?.recent_tasks || payload.recent_tasks || [],
    }));
    setConfig((current) => configFromPayload(payload, current));
  };

  const saveConfig = async () => {
    setSaving("config");
    const patch = buildCoworkingPatch(config);
    try {
      try {
        const { data: payload } = await api.patch("/admin/coworking/settings", patch);
        applyAdminPayload(payload);
        toast.success("Coworking settings saved.");
      } catch (err) {
        // Pre-polish backends expose coworking only via PATCH /settings.
        if (err?.response?.status !== 404) throw err;
        await api.patch("/settings", { coworking: patch });
        const { data: payload } = await api.get("/admin/coworking");
        applyAdminPayload(payload);
        toast.success("Coworking settings saved. Redeploy the backend so Admin uses /admin/coworking/settings.");
      }
    } catch (err) {
      toast.error(coworkingApiError(err, "Could not save coworking."));
    } finally {
      setSaving("");
    }
  };

  const runTimer = async (action) => {
    setSaving(`timer-${action}`);
    try {
      const { data: payload } = await api.post("/admin/coworking/timer", { action });
      if (payload?.state) {
        setData((current) => ({ ...(current || {}), ...payload.state }));
      }
      toast.success(payload?.message || `Timer ${action}.`);
    } catch (err) {
      toast.error(coworkingApiError(err, `Could not ${action} timer.`));
    } finally {
      setSaving("");
    }
  };

  const clearTasks = async () => {
    setSaving("clear");
    try {
      const { data: payload } = await api.delete("/admin/coworking/tasks");
      toast.success(`Cleared ${payload?.deleted ?? 0} task(s).`);
      await load();
    } catch (err) {
      toast.error(coworkingApiError(err, "Could not clear tasks."));
    } finally {
      setSaving("");
    }
  };

  if (loading && !data) {
    return <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">Loading coworking...</div>;
  }

  const openTasks = Array.isArray(data?.tasks) ? data.tasks : [];
  const recentTasks = Array.isArray(data?.recent_tasks) ? data.recent_tasks : [];
  const prefix = data?.command_prefix || "!";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="space-y-6" data-testid="coworking-admin-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Stream tools</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Timer className="w-6 h-6 text-[hsl(var(--primary))]" /> Coworking
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Focus timer and viewer task board for co-working streams. Default is off. OBS: timer + stats under the header on the left; tasks under the header on the right (keep clear of shoutout/raid toasts).
          </p>
        </div>
        <button type="button" onClick={load} className={`${secondaryButton} h-10 px-4 inline-flex items-center gap-2`}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <section className="surface p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="label-eyebrow">Suite</div>
            <h2 className="text-lg font-semibold mt-1">Enable and lengths</h2>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(config.enabled)}
              onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))}
              data-testid="coworking-admin-enabled"
            />
            Enabled for chat + overlays
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <div className="label-eyebrow mb-1.5">Work minutes</div>
            <input
              type="number"
              min={1}
              max={180}
              className={inputClass}
              value={config.work_minutes}
              onChange={(event) => setConfig((current) => ({ ...current, work_minutes: event.target.value }))}
              data-testid="coworking-admin-work-minutes"
            />
          </label>
          <label className="block">
            <div className="label-eyebrow mb-1.5">Break minutes</div>
            <input
              type="number"
              min={1}
              max={60}
              className={inputClass}
              value={config.break_minutes}
              onChange={(event) => setConfig((current) => ({ ...current, break_minutes: event.target.value }))}
              data-testid="coworking-admin-break-minutes"
            />
          </label>
          <label className="block">
            <div className="label-eyebrow mb-1.5">Max tasks per viewer</div>
            <input
              type="number"
              min={1}
              max={20}
              className={inputClass}
              value={config.max_tasks_per_viewer}
              onChange={(event) => setConfig((current) => ({ ...current, max_tasks_per_viewer: event.target.value }))}
              data-testid="coworking-admin-max-per-viewer"
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={config.confetti_sound !== false}
            onChange={(event) => setConfig((current) => ({ ...current, confetti_sound: event.target.checked }))}
            data-testid="coworking-admin-confetti-sound"
          />
          Confetti sound on task complete
        </label>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          OBS Browser Source needs Control audio via OBS enabled (and not muted) for the blip to hit stream. Autoplay blocks fail soft.
        </p>
        <button
          type="button"
          onClick={saveConfig}
          disabled={saving === "config"}
          className={`${primaryButton} h-10 px-4 inline-flex items-center gap-2`}
          data-testid="coworking-admin-save"
        >
          <Save className="w-4 h-4" /> Save settings
        </button>
      </section>

      <section className="surface p-5 space-y-4">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <div className="label-eyebrow">Focus timer</div>
            <div className="mt-2 text-4xl font-semibold tracking-tight font-mono" data-testid="coworking-admin-clock">
              {timer.display || "00:00"}
            </div>
            <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              {timer.phase === "break" ? "Break" : "Focus"} · {timer.status || "idle"} · cycle {timer.cycle_count ?? 0}
              {" · "}
              {data?.stats?.people_coworking ?? 0} coworking · {data?.stats?.tasks_completed ?? 0} done this session
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => runTimer("start")} disabled={Boolean(saving)} className={`${primaryButton} h-10 px-3 inline-flex items-center gap-2`} data-testid="coworking-admin-start">
              <Play className="w-4 h-4" /> Start
            </button>
            <button type="button" onClick={() => runTimer("pause")} disabled={Boolean(saving)} className={`${secondaryButton} h-10 px-3 inline-flex items-center gap-2`} data-testid="coworking-admin-pause">
              <Pause className="w-4 h-4" /> Pause
            </button>
            <button type="button" onClick={() => runTimer("reset")} disabled={Boolean(saving)} className={`${secondaryButton} h-10 px-3 inline-flex items-center gap-2`} data-testid="coworking-admin-reset">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button type="button" onClick={() => runTimer("skip")} disabled={Boolean(saving)} className={`${secondaryButton} h-10 px-3 inline-flex items-center gap-2`} data-testid="coworking-admin-skip">
              <SkipForward className="w-4 h-4" /> Skip
            </button>
          </div>
        </div>
        <div className="h-2 rounded bg-[hsl(var(--surface-2))] overflow-hidden">
          <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${Math.round((timer.progress || 0) * 100)}%` }} />
        </div>
      </section>

      <section className="surface p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="label-eyebrow">Open tasks</div>
            <h2 className="text-lg font-semibold mt-1 flex items-center gap-2">
              <Users className="w-4 h-4 text-[hsl(var(--primary))]" /> Board ({openTasks.length})
            </h2>
          </div>
          <button
            type="button"
            onClick={clearTasks}
            disabled={saving === "clear"}
            className={`${secondaryButton} h-10 px-3 inline-flex items-center gap-2`}
            data-testid="coworking-admin-clear-tasks"
          >
            <Trash2 className="w-4 h-4" /> Clear tasks
          </button>
        </div>
        {openTasks.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="coworking-admin-empty-tasks">
            No open tasks. Viewers add with {prefix}task.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="coworking-admin-open-tasks">
            {openTasks.map((task) => (
              <li
                key={task.id}
                className="flex items-center gap-3 p-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]"
              >
                <CoworkingTaskAvatar
                  avatarUrl={task.avatar_url}
                  name={taskLabel(task)}
                  size={32}
                  className="border border-[hsl(var(--border))]"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{taskLabel(task)}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] truncate">{task.text}</div>
                </div>
                <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">#{task.index || ""}</span>
              </li>
            ))}
          </ul>
        )}
        {recentTasks.some((task) => task.status === "done") && (
          <div className="pt-2">
            <div className="label-eyebrow mb-2">Recently done</div>
            <ul className="space-y-2">
              {recentTasks
                .filter((task) => task.status === "done")
                .slice(0, 6)
                .map((task) => (
                  <li key={`done-${task.id}`} className="flex items-center gap-3 text-sm text-[hsl(var(--muted-foreground))]">
                    <CheckCircle2 className="w-4 h-4 text-[#30d158] shrink-0" />
                    <CoworkingTaskAvatar
                      avatarUrl={task.avatar_url}
                      name={taskLabel(task)}
                      size={24}
                    />
                    <span className="truncate">
                      <span className="text-white">{taskLabel(task)}</span>: {task.text}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </section>

      <section className="surface p-5 space-y-3">
        <div className="label-eyebrow">OBS + chat</div>
        <div className="space-y-1 font-mono text-xs text-[hsl(var(--primary))] break-all">
          <div>{origin}/overlay/coworking/timer</div>
          <div>{origin}/overlay/coworking/tasks</div>
          <div className="text-[hsl(var(--muted-foreground))]">{origin}/overlay/coworking (combined)</div>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Chat: {prefix}coworking (help), {prefix}task, {prefix}done, {prefix}tasks. Mods: {prefix}focus start|pause|reset|skip|status, {prefix}cleartasks.
        </p>
      </section>
    </div>
  );
}
